import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Command, Brain, Pin, ArrowRight, X } from 'lucide-react';
import { BRAND, AGENTS } from '../../lib/brand';
import { Eyebrow } from '../instrument';

const STORAGE_KEY = 'cognara.onboarding.completed';
const LEGACY_STORAGE_KEY = 'capexara.onboarding.completed';

const steps = [
  {
    icon: Sparkles,
    eyebrow: `Welcome to ${BRAND.name}`,
    title: 'Your expert engine is live',
    body: `${AGENTS.sutradhar.display} is your orchestrator. Route any question, run deep research across your knowledge base, and generate operator-ready documents — all from one thread.`,
    hint: 'Continue →',
  },
  {
    icon: Command,
    eyebrow: 'Press ⌘K anywhere',
    title: 'Command Bar is your cockpit',
    body: 'Search projects, jump to any page, or "Ask Cognara" directly — without ever leaving the keyboard. ⌘K is your single door in.',
    hint: 'Press ⌘K to try it (or continue) →',
  },
  {
    icon: Brain,
    eyebrow: 'Audit-grade memory',
    title: 'Long threads stay precise',
    body: 'Older turns auto-compress to a running summary once a conversation exceeds 60K tokens. Click the Memory pill in any chat header to inspect the stored memory.',
    hint: 'Continue →',
  },
  {
    icon: Pin,
    eyebrow: 'Pin-a-Fact',
    title: 'Facts that never drift',
    body: 'Pin contract clauses, safety rules, or budget limits directly into a conversation. Pinned facts are injected verbatim into every reply — never aged out, never rewritten.',
    hint: 'Get started →',
  },
];

export const FirstRunOnboarding = ({ userName, scope = 'portal' }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Only show for portal users; admin users skip.
    if (scope !== 'portal') return;
    try {
      const done = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!done) {
        setTimeout(() => setOpen(true), 900);
      }
    } catch {}
  }, [scope]);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setOpen(false);
  };

  const next = () => {
    if (step < steps.length - 1) setStep((s) => s + 1);
    else close();
  };

  const current = steps[step];
  const Icon = current.icon;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[90]"
            style={{
              background:
                'radial-gradient(ellipse 60% 40% at 50% 40%, rgba(30,78,140,0.25), transparent 70%), rgba(5, 8, 15, 0.85)',
              backdropFilter: 'blur(16px)',
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[91] flex items-center justify-center p-6 pointer-events-none"
            role="dialog"
            aria-modal="true"
            data-testid="onboarding"
          >
            <div
              className="w-full max-w-[540px] pointer-events-auto rounded-2xl overflow-hidden"
              style={{
                background: 'var(--layer-3)',
                border: '1px solid rgba(30,78,140,0.4)',
                boxShadow: '0 30px 80px -20px rgba(30,78,140,0.5), var(--inner-glow)',
              }}
            >
              {/* Progress */}
              <div className="flex items-center gap-1.5 px-6 pt-5 mb-4">
                {steps.map((_, i) => (
                  <motion.div
                    key={i}
                    initial={false}
                    animate={{
                      width: i === step ? 28 : 12,
                      background: i <= step ? '#2E70AD' : 'rgba(255,255,255,0.08)',
                    }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="h-1 rounded-full"
                  />
                ))}
                <div className="flex-1" />
                <button onClick={close}
                        className="p-1 -m-1 text-nexus-text-tertiary hover:text-nexus-text-primary transition-colors"
                        data-testid="onboarding-close">
                  <X size={14} />
                </button>
              </div>

              <div className="px-8 pb-8">
                {/* Icon */}
                <motion.div
                  key={step}
                  initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30,78,140,0.2), rgba(242,107,31,0.2))',
                    border: '1px solid rgba(30,78,140,0.35)',
                    boxShadow: 'var(--inner-glow)',
                  }}
                >
                  <Icon size={22} style={{ color: '#5790C9' }} strokeWidth={1.75} />
                </motion.div>

                <motion.div
                  key={`t-${step}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.05 }}
                >
                  <Eyebrow>{current.eyebrow}</Eyebrow>
                  <h2 className="ig-display text-[28px] mt-2 mb-3 text-nexus-text-primary tracking-[-0.02em]">
                    {current.title}
                  </h2>
                  <p className="text-[14.5px] leading-[1.65] text-nexus-text-secondary">
                    {current.body}
                  </p>
                </motion.div>

                {/* Footer */}
                <div className="flex items-center justify-between mt-8">
                  <div className="ig-mono text-[11px] text-nexus-text-tertiary">
                    Step {step + 1} of {steps.length}
                  </div>
                  <div className="flex items-center gap-2">
                    {step > 0 && (
                      <button onClick={() => setStep((s) => s - 1)}
                              className="h-9 px-3 rounded-lg text-[12px] text-nexus-text-secondary hover:text-nexus-text-primary transition-colors">
                        Back
                      </button>
                    )}
                    <button
                      onClick={next}
                      className="ig-cta h-9 px-4 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5"
                      style={{ background: '#1E4E8C', color: '#E6EEF9', boxShadow: 'var(--tinted-shadow-cta)' }}
                      data-testid="onboarding-next"
                    >
                      {step === steps.length - 1 ? 'Start' : 'Continue'} <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FirstRunOnboarding;
